import React, { useState } from "react";
import axios from "axios";
import { Send, Mail, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Basic — $125 (3 days)",
  "Standard — $275 (5 days)",
  "Premium — $550 (7 days)",
  "Not sure yet — let's talk",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: PROJECT_TYPES[0],
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrMsg("Please fill in your name, email, and a short message.");
      return;
    }
    setStatus("sending");
    setErrMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(
        err?.response?.data?.detail ||
          "Something went wrong on our end. Please email support@melavatestudios.com directly."
      );
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-plum-deepest section-pad relative overflow-hidden">
      <span aria-hidden className="hero-monogram" style={{ bottom: "-15%", right: "-5%", color: "rgba(201,169,97,0.07)" }}>
        &amp;
      </span>

      <div className="container-edit relative">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold mb-5">Start a Project</div>
            <h2 className="h-section text-cream mb-6">
              Let's build <span className="font-display-italic text-gold">something good.</span>
            </h2>
            <p className="text-cream/75 text-lg leading-relaxed mb-10">
              Tell me what you're working on. I'll come back within 24 hours with a clear
              scope, a fixed price, and an honest answer on whether we're the right fit.
            </p>

            <div className="space-y-5 pt-8 border-t border-gold/20">
              <ContactInfo
                icon={<Mail size={18} />}
                label="Email"
                value="support@melavatestudios.com"
              />
              <ContactInfo
                icon={<span className="text-gold font-display-italic text-lg leading-none">F</span>}
                label="Fiverr"
                value="fiverr.com/melavatestudios"
              />
              <ContactInfo
                icon={<span className="text-gold font-display-italic text-lg leading-none">⌛</span>}
                label="Reply Time"
                value="Within 24 hours, always"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-cream rounded-2xl p-8 md:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow text-plum mb-2 block" htmlFor="contact-name">
                    Your Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name"
                    type="text"
                    className="input-edit"
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="eyebrow text-plum mb-2 block" htmlFor="contact-email">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email"
                    type="email"
                    className="input-edit"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="eyebrow text-plum mb-2 block" htmlFor="contact-type">
                  What can I help with?
                </label>
                <select
                  id="contact-type"
                  data-testid="contact-type"
                  className="input-edit"
                  value={form.project_type}
                  onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                >
                  {PROJECT_TYPES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="eyebrow text-plum mb-2 block" htmlFor="contact-message">
                  Tell me about your project
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message"
                  rows="5"
                  className="input-edit resize-none"
                  placeholder="A few sentences about what you need, your timeline, and any details that would help me scope it."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              {status === "success" && (
                <div
                  data-testid="contact-success"
                  className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm"
                >
                  <Check size={18} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Got it. Melanie will get back to you within 24 hours at the email you
                    provided. Talk soon!
                  </span>
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid="contact-error"
                  className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm"
                >
                  <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{errMsg}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === "sending"}
                className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? "Sending..." : "Send Message"}
                <Send size={17} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactInfo({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center text-gold flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="eyebrow text-cream/60 mb-1">{label}</div>
        <div className="text-cream font-medium">{value}</div>
      </div>
    </div>
  );
}
